import { isWebView } from '@/utils/mobile/RNPlugins';
import { NATIVE_INTERACTION_MESSAGE_TYPE } from '@/utils/mobile/type.messages';

export class NativePostMessageEvent<T> {
  private callback: () => void;
  private ttl: number | null = 10000;

  private constructor(private type: NATIVE_INTERACTION_MESSAGE_TYPE) {
    this.callback = () => window.ReactNativeWebView.postMessage(JSON.stringify({ type }));
  }

  private webViewCustomRequest<T>(
    callback: () => void,
    type: NATIVE_INTERACTION_MESSAGE_TYPE,
    timeout: number | null = 10000
  ): Promise<T> {
    if (!isWebView()) {
      throw new Error('Native Plugin을 확인할 수 없습니다.');
    } else {
      return new Promise<T>((resolve, reject) => {
        const timer = timeout
          ? setTimeout(() => {
              reject(new Error(`type: ${type}의 요청이 응답 가능 시간을 벗어났습니다.`));
            }, timeout)
          : null;

        const handler = (event: MessageEvent) => {
          if (timer) {
            clearTimeout(timer);
          }

          const {
            data,
            type: responseType,
          }: {
            type: NATIVE_INTERACTION_MESSAGE_TYPE;
            data: T;
          } = JSON.parse(event.data);

          if (responseType === type) {
            resolve(data);
            document.removeEventListener('message', handler as EventListener);
            window.removeEventListener('message', handler);
          }
        };
        document.addEventListener('message', handler as EventListener);
        window.addEventListener('message', handler);
        callback();
      });
    }
  }

  async getOrNull(): Promise<T | null> {
    try {
      return await this.webViewCustomRequest<T>(this.callback, this.type, this.ttl);
    } catch {
      return null;
    }
  }

  getOrThrow(): Promise<T> {
    return this.webViewCustomRequest<T>(this.callback, this.type, this.ttl);
  }

  async getOrDefault(defaultValue: T): Promise<T> {
    try {
      return await this.webViewCustomRequest<T>(this.callback, this.type, this.ttl);
    } catch {
      return defaultValue;
    }
  }

  static Builder = class NativeEventBuilder<T> {
    private readonly event: NativePostMessageEvent<T>;

    constructor(type: NATIVE_INTERACTION_MESSAGE_TYPE) {
      this.event = new NativePostMessageEvent<T>(type);
    }

    setPayload<P>(payload: P) {
      this.event.callback = () =>
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: this.event.type,
            payload,
          })
        );
      return this;
    }

    setTimeoutTTL(ttl: number | null) {
      if (ttl === Infinity) {
        this.event.ttl = null;
        return this;
      }

      this.event.ttl = ttl;
      return this;
    }

    build(): NativePostMessageEvent<T> {
      return this.event;
    }
  };
}
